/**
 * @author Charles Carlson
 * @copyright Copyright 2026 by Radivision Inc., CA, USA. All Rights Reserved.
 * @Date: 2026-08-18
 * @description Implementation of consent store interface, returns and sets the state of consent for ga4.
 * @filename gtag-consent-store.ts
 */
import { ConsentSignals, ConsentStatus, getPolicyForRegion } from "./gtag-consent-policy";
import { hasCookieConsentDecision } from "./cookie-consent-storage";
type ConsentCategory = keyof ConsentSignals;
function pushConsentToGtag(state: ConsentSignals): void {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(["consent", "update", { ...state }]);
}
//Interface of Consent Store, initializes object as requiring banner and setting implicit deny.
interface ConsentStoreContract {
  initialize(country: string, subdivision?: string | null): { requiresBanner: boolean; defaults: ConsentSignals };
  set(category: ConsentCategory, status: ConsentStatus): void;
  get(category: ConsentCategory): ConsentStatus;
  requiresBanner(): boolean;
  ready: Promise<{ requiresBanner: boolean; defaults: ConsentSignals }>;
}
class ConsentStore implements ConsentStoreContract {
  private currentState: ConsentSignals;
  private bannerRequired: boolean;
  private resolveReady: (result: { requiresBanner: boolean; defaults: ConsentSignals }) => void;
  ready: Promise<{ requiresBanner: boolean; defaults: ConsentSignals }>;

  constructor() {
    this.currentState = {
      ad_user_data: "denied",
      ad_personalization: "denied",
      ad_storage: "denied",
      analytics_storage: "denied",
    };
    this.bannerRequired = true;
    this.ready = new Promise((resolve) => {
      this.resolveReady = resolve;
    });
  }
  //This init method will be used for determining banner requirement and default consent status based on region policy
  initialize(country: string, subdivision?: string | null): { requiresBanner: boolean; defaults: ConsentSignals } {
    const policy = getPolicyForRegion(country, subdivision);
    this.bannerRequired = policy.requiresBanner;
    if (!hasCookieConsentDecision()) {
      this.currentState = policy.getDefaults();
      pushConsentToGtag(this.currentState);
    }
    const result = { requiresBanner: this.bannerRequired, defaults: this.currentState };
    this.resolveReady(result);
    return result;
  }

  //Sets one of the four categories  to a determind consent status based on banner
  set(category: ConsentCategory, status: ConsentStatus): void {
    this.currentState[category] = status;
  }
  //Returns the status of consent for a category
  get(category: ConsentCategory): ConsentStatus {
    return this.currentState[category];
  }
  //Returns booleans for whether or not to show the banner
  requiresBanner(): boolean {
    return this.bannerRequired;
  }
}
//Creates a single consent store object during a session, will be later initialized upon render of page
const consentStore: ConsentStoreContract = new ConsentStore();

export { consentStore, ConsentCategory, ConsentStoreContract, ConsentStore, pushConsentToGtag };
